import { EffectComposer } from '@react-three/postprocessing';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import CloudLayer, { type CloudLayerRef } from '0_canvas/CloudLayer/CloudLayer';
import { DropletsEffect } from '0_canvas/effects/DropletsEffect';
import { FlyingDebris } from '0_canvas/FlyingDebris/FlyingDebris';

import CycloneEffect, { type CycloneEffectRef } from './CycloneEffect';

export interface CyclonePerilProps {
  animationProgress?: number;
}

export interface CyclonePerilRef {
  animationProgress: number;
}

const CyclonePeril = forwardRef<CyclonePerilRef, CyclonePerilProps>(
  ({ animationProgress = 1 }, ref) => {
    const animationProgressRef = useRef<number>(animationProgress);
    const cycloneEffectRef = useRef<CycloneEffectRef>(null!);
    const cloudLayerRef = useRef<CloudLayerRef>(null!);
    const [isActive, setIsActive] = useState(animationProgress > 0);

    useImperativeHandle(ref, () => ({
      get animationProgress() {
        return animationProgressRef.current;
      },
      set animationProgress(n: number) {
        animationProgressRef.current = n;
        cycloneEffectRef.current && (cycloneEffectRef.current.opacity = n);
        cloudLayerRef.current.animationProgress = n;

        if (n > 0 && !isActive) setIsActive(true);
        if (n <= 0 && isActive) setIsActive(false);
      },
    }));

    return (
      <>
        <EffectComposer>
          <CycloneEffect
            ref={cycloneEffectRef}
            opacity={animationProgress / 2}
          />
          {isActive ? <DropletsEffect /> : <></>}
        </EffectComposer>
        {isActive && <FlyingDebris />}
        <CloudLayer
          ref={cloudLayerRef}
          size={2000}
          position={[0, 250, 0]}
          animationProgress={animationProgress}
        />
      </>
    );
  }
);

CyclonePeril.displayName = 'CyclonePeril';
export default CyclonePeril;
